<script setup lang="ts">
  import {
    italianDateFormat,
    italianMonthYearFormat,
    useLocale,
  } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { VueDatePicker } from '@vuepic/vue-datepicker';
  import type { MonthModel } from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';
  import { format } from 'date-fns';
  import { it } from 'date-fns/locale';
  import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    useTemplateRef,
    watch,
  } from 'vue';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import { TextInput } from '../TextInput';
  import './DatePicker-override.css';
  import {
    calendarIconClass,
    customDatePickerMenuClass,
    datePickerWrapperClass,
  } from './DatePicker.css';
  import { formatDateInput, formatMonthInput } from './dateInput.utils';
  import type { DatePickerWithModelValue } from './DatePicker.types';

  type DatePickerInstance = {
    closeMenu: () => void;
    openMenu: () => void;
    selectDate: () => void;
  };

  const props = withDefaults(defineProps<DatePickerWithModelValue>(), {
    teleport: false,
    monthPicker: false,
  });

  const { currentLanguageDateFormat } = useLocale();

  const emit = defineEmits<{
    'update:modelValue': [Date | undefined];
    blur: [FocusEvent | undefined];
  }>();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const datepicker = useTemplateRef<DatePickerInstance>('datepicker');
  const debounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
  const inputValue = ref<string>('');
  const isMenuOpen = ref(false);

  const displayFormat = computed(() =>
    props.monthPicker
      ? italianMonthYearFormat
      : currentLanguageDateFormat.value,
  );

  const pickerFormat = computed(() =>
    props.monthPicker ? italianMonthYearFormat : italianDateFormat,
  );

  const pickerModelValue = computed(() => {
    if (!props.monthPicker) return props.modelValue;
    if (!props.modelValue) return undefined;

    return {
      month: props.modelValue.getMonth(),
      year: props.modelValue.getFullYear(),
    };
  });

  watch(
    [() => props.modelValue, displayFormat],
    ([newValue, newFormat]) => {
      if (!newValue) {
        inputValue.value = '';
        return;
      }

      inputValue.value = format(newValue, newFormat, {
        locale: it,
      });
    },
    { immediate: true },
  );

  const normalizeDate = (date: Date) =>
    new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
    );

  const monthModelToDate = (value: MonthModel) =>
    new Date(Number(value.year), Number(value.month), 1);

  const handleDateSelect = (value: Date | MonthModel | undefined) => {
    if (!value) {
      emit('update:modelValue', undefined);
      return;
    }

    const date = value instanceof Date ? value : monthModelToDate(value);

    emit('update:modelValue', normalizeDate(date));
  };

  const handleInput = (date: string | undefined) => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);

    if (!date) {
      inputValue.value = '';
      emit('update:modelValue', undefined);
      return;
    }

    inputValue.value = date;

    debounceTimeout.value = setTimeout(() => {
      const parsedDate = props.monthPicker
        ? formatMonthInput(date)
        : formatDateInput(date);
      if (!parsedDate) return;

      const normalizedDate = normalizeDate(parsedDate);

      inputValue.value = format(normalizedDate, displayFormat.value, {
        locale: it,
      });
      datepicker.value!.closeMenu();
      handleDateSelect(normalizedDate);
    }, 500);
  };

  const onBlur = (event?: FocusEvent) => {
    if (isMenuOpen.value) return;
    emit('blur', event);
  };

  const onMenuOpen = () => {
    isMenuOpen.value = true;
  };

  const onMenuClosed = () => {
    isMenuOpen.value = false;
    emit('blur', undefined);
  };

  onMounted(() => {
    if (props.autofocus && datepicker.value) {
      datepicker.value.openMenu();
    }
  });

  onUnmounted(() => {
    if (debounceTimeout.value) clearTimeout(debounceTimeout.value);
  });

  defineOptions({
    inheritAttrs: false,
  });
</script>

<template>
  <div :class="datePickerWrapperClass">
    <VueDatePicker
      :id="id"
      ref="datepicker"
      :name="name"
      :locale="it"
      autocomplete="off"
      :format-locale="it"
      :min-date="minDate"
      :max-date="maxDate"
      :format="pickerFormat"
      :month-picker="monthPicker"
      :prevent-min-max-navigation="preventMinMaxNavigation"
      :start-date="startDate"
      :focus-start-date="focusStartDate"
      :enable-time-picker="false"
      :disabled="disabled"
      :model-value="pickerModelValue"
      :menu-class-name="customDatePickerMenuClass"
      :clearable="false"
      :timezone="timeZone"
      :auto-apply="true"
      :no-swipe="true"
      text-input
      :autofocus="autofocus"
      :teleport="teleport"
      @update:model-value="handleDateSelect"
      @open="onMenuOpen"
      @closed="onMenuClosed"
      v-bind="$attrs"
    >
      <template #clear-icon><span /></template>
      <template #dp-input>
        <TextInput
          :id="`date-picker-input-${name}`"
          :name="name"
          :label="label"
          :helper-text="helperText"
          :placeholder="placeholder"
          :error="error"
          :warning="warning"
          :disabled="disabled"
          :disabled-reason="disabledReason"
          :required="required"
          :size="size"
          :model-value="inputValue"
          @update:model-value="handleInput"
          @blur="onBlur"
        >
          <template #icon>
            <FontIcon
              :class="calendarIconClass"
              :icon-name="TUNDR_ICONS.CALENDAR"
            />
          </template>
        </TextInput>
      </template>
    </VueDatePicker>
  </div>
</template>
